import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Upload,
  FileText,
  X,
  Loader2,
  Search,
  Check,
  Eye,
  Sparkles,
  AlertCircle,
  Calendar,
  Building2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import { toast } from "sonner";
import type { Tables, Database } from "@/integrations/supabase/types";

type DocumentType = Database["public"]["Enums"]["document_type"];

const DOC_TYPE_LABELS: Record<string, string> = {
  invoice: "Facture",
  contract: "Contrat",
  identity: "Identité",
  tax: "Fiscalité",
  insurance: "Assurance",
  receipt: "Reçu",
  devis: "Devis",
  attestation: "Attestation",
  bank: "Banque",
  medical: "Médical",
  other: "Autre",
};

interface ExtractedData {
  title: string | null;
  doc_type: string | null;
  doc_date: string | null;
  expires_at: string | null;
}

interface Document {
  id: string;
  title: string;
  file_name: string;
  file_path: string;
  doc_type: string;
  issuer_name: string | null;
  doc_date: string | null;
  created_at: string;
  score?: number;
}

interface TaskDocumentLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskLabel: string;
  onDocumentLinked: (docId: string, docTitle: string) => void;
}

export function TaskDocumentLinkDialog({
  open,
  onOpenChange,
  taskLabel,
  onDocumentLinked,
}: TaskDocumentLinkDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { openDocument } = useDocumentPreview();

  const [searchQuery, setSearchQuery] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  // AI analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisError, setAnalysisError] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [customTitle, setCustomTitle] = useState("");

  // Reset states on close or file change
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setShowUpload(false);
      setFile(null);
      setIsAnalyzing(false);
      setAnalysisComplete(false);
      setAnalysisError(false);
      setExtractedData(null);
      setCustomTitle("");
    } else {
      // Pre-fill search with keywords from task label
      const keywords = extractKeywords(taskLabel);
      setSearchQuery(keywords.join(" "));
    }
  }, [open, taskLabel]);

  useEffect(() => {
    if (!file) {
      setIsAnalyzing(false);
      setAnalysisComplete(false);
      setAnalysisError(false);
      setExtractedData(null);
      setCustomTitle("");
    }
  }, [file]);

  // Extract keywords from task label for smart suggestions
  const extractKeywords = (label: string): string[] => {
    const commonWords = new Set([
      "de", "du", "des", "le", "la", "les", "un", "une", "et", "ou", "à", "en",
      "pour", "par", "sur", "avec", "dans", "attestation", "certificat", "document",
      "pièce", "justificatif", "copie", "original", "fournir", "ajouter", "obtenir"
    ]);

    return label
      .toLowerCase()
      .split(/\s+/)
      .filter(word => word.length > 2 && !commonWords.has(word))
      .slice(0, 3);
  };

  // Score documents based on relevance to task label
  const scoreDocument = (doc: Document): number => {
    const labelLower = taskLabel.toLowerCase();
    const titleLower = doc.title.toLowerCase();
    const issuerLower = (doc.issuer_name || "").toLowerCase();
    const docType = doc.doc_type;

    let score = 0;

    // Title match
    if (titleLower.includes(labelLower) || labelLower.includes(titleLower)) {
      score += 50;
    }

    // Keyword matches
    const keywords = extractKeywords(taskLabel);
    keywords.forEach(keyword => {
      if (titleLower.includes(keyword)) score += 20;
      if (issuerLower.includes(keyword)) score += 15;
    });

    // Document type relevance
    const typeMapping: Record<string, string[]> = {
      attestation: ["attestation", "certificat", "justificatif"],
      insurance: ["assurance", "garantie", "police"],
      tax: ["impôt", "fiscal", "taxe", "tva"],
      bank: ["banque", "rib", "bancaire", "compte"],
      identity: ["identité", "carte", "passeport", "kbis"],
      contract: ["contrat", "convention", "accord"],
      invoice: ["facture", "paiement"],
    };

    Object.entries(typeMapping).forEach(([type, words]) => {
      if (docType === type && words.some(w => labelLower.includes(w))) {
        score += 30;
      }
    });

    // Recency bonus
    const daysSinceCreation = (Date.now() - new Date(doc.created_at).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceCreation < 30) score += 10;
    if (daysSinceCreation < 7) score += 5;

    return score;
  };

  // Fetch existing documents
  const { data: documents = [], isLoading: loadingDocs } = useQuery({
    queryKey: ["task-link-documents", searchQuery],
    queryFn: async () => {
      if (!user) return [];

      let query = supabase
        .from("documents")
        .select("id, title, file_name, file_path, doc_type, issuer_name, doc_date, created_at")
        .eq("user_id", user.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(50);

      if (searchQuery.trim()) {
        query = query.ilike("title", `%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Document[];
    },
    enabled: !!user && open && !showUpload,
  });

  // Sort documents by relevance
  const sortedDocuments = documents
    .map(doc => ({ ...doc, score: scoreDocument(doc) }))
    .sort((a, b) => b.score - a.score);

  const suggestedDocs = sortedDocuments.filter(d => d.score > 20).slice(0, 3);
  const otherDocs = sortedDocuments.filter(d => !suggestedDocs.includes(d));

  // AI Analysis function
  const analyzeDocument = useCallback(async (fileToAnalyze: File) => {
    if (!user) return;

    setIsAnalyzing(true);
    setAnalysisComplete(false);
    setAnalysisError(false);
    setExtractedData(null);

    try {
      const fileExt = fileToAnalyze.name.split(".").pop();
      const tempPath = `temp/${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(tempPath, fileToAnalyze);

      if (uploadError) throw uploadError;

      const { data: result, error: extractError } = await supabase.functions.invoke(
        "extract-document-info",
        {
          body: {
            filePath: tempPath,
            fileName: fileToAnalyze.name,
            mimeType: fileToAnalyze.type,
          },
        }
      );

      await supabase.storage.from("documents").remove([tempPath]);

      if (extractError) throw extractError;

      const extracted: ExtractedData = {
        title: result?.title || null,
        doc_type: result?.doc_type || null,
        doc_date: result?.doc_date || null,
        expires_at: result?.expires_at || null,
      };

      setExtractedData(extracted);
      setCustomTitle(extracted.title || fileToAnalyze.name.replace(/\.[^/.]+$/, ""));
      setAnalysisComplete(true);
    } catch (error) {
      console.error("AI analysis error:", error);
      setAnalysisError(true);
      setCustomTitle(fileToAnalyze.name.replace(/\.[^/.]+$/, ""));
      setAnalysisComplete(true);
    } finally {
      setIsAnalyzing(false);
    }
  }, [user]);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (fileToUpload: File) => {
      if (!user) throw new Error("Non authentifié");

      const fileExt = fileToUpload.name.split(".").pop();
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, fileToUpload);

      if (uploadError) throw uploadError;

      const finalTitle = customTitle.trim() || extractedData?.title || fileToUpload.name.replace(/\.[^/.]+$/, "");

      const insertPayload: {
        user_id: string;
        title: string;
        file_name: string;
        file_path: string;
        mime_type: string;
        file_size: number;
        source: "upload";
        doc_type?: DocumentType | null;
        doc_date?: string | null;
        expires_at?: string | null;
      } = {
        user_id: user.id,
        title: finalTitle,
        file_name: fileToUpload.name,
        file_path: filePath,
        mime_type: fileToUpload.type,
        file_size: fileToUpload.size,
        source: "upload",
      };

      if (extractedData?.doc_type) {
        insertPayload.doc_type = extractedData.doc_type as DocumentType;
      }
      if (extractedData?.doc_date) {
        insertPayload.doc_date = extractedData.doc_date;
      }
      if (extractedData?.expires_at) {
        insertPayload.expires_at = extractedData.expires_at;
      }

      const { data: newDoc, error: insertError } = await supabase
        .from("documents")
        .insert([insertPayload])
        .select("id, title")
        .single();

      if (insertError) throw insertError;
      return newDoc;
    },
    onSuccess: (newDoc) => {
      toast.success("Document importé et lié !");
      queryClient.invalidateQueries({ queryKey: ["task-link-documents"] });
      onDocumentLinked(newDoc.id, newDoc.title);
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Erreur lors de l'import");
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const droppedFile = acceptedFiles[0];
      setFile(droppedFile);
      analyzeDocument(droppedFile);
    }
  }, [analyzeDocument]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".heic", ".webp"],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
    disabled: isAnalyzing,
  });

  const handleSelectDocument = (doc: Document) => {
    onDocumentLinked(doc.id, doc.title);
    onOpenChange(false);
  };

  const handlePreview = (doc: Document) => {
    openDocument(doc as unknown as Tables<"documents">);
  };

  const handleUpload = () => {
    if (!file) return;
    uploadMutation.mutate(file);
  };

  const isProcessing = uploadMutation.isPending || isAnalyzing;
  const canUpload = file && analysisComplete && !uploadMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !isProcessing && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Lier un document</DialogTitle>
          <DialogDescription className="line-clamp-2">
            Pour : <span className="font-medium text-foreground">{taskLabel}</span>
          </DialogDescription>
        </DialogHeader>

        {!showUpload ? (
          /* Search & Link Existing View */
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher dans vos documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Results */}
            <ScrollArea className="h-[280px] -mx-2 px-2">
              {loadingDocs ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Suggested matches */}
                  {suggestedDocs.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-primary" />
                        Correspondances suggérées
                      </p>
                      <div className="space-y-1.5">
                        {suggestedDocs.map((doc) => (
                          <DocumentRow
                            key={doc.id}
                            document={doc}
                            onSelect={() => handleSelectDocument(doc)}
                            onPreview={() => handlePreview(doc)}
                            highlighted
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Other documents */}
                  {otherDocs.length > 0 && (
                    <div>
                      {suggestedDocs.length > 0 && (
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Autres documents
                        </p>
                      )}
                      <div className="space-y-1.5">
                        {otherDocs.map((doc) => (
                          <DocumentRow
                            key={doc.id}
                            document={doc}
                            onSelect={() => handleSelectDocument(doc)}
                            onPreview={() => handlePreview(doc)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {sortedDocuments.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun document trouvé</p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Import new button */}
            <Button
              variant="outline"
              className="w-full gap-2 border-dashed"
              onClick={() => setShowUpload(true)}
            >
              <Upload className="w-4 h-4" />
              Importer un nouveau document
            </Button>
          </div>
        ) : (
          /* Upload View */
          <div className="space-y-4">
            {/* Back button */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 -ml-2 text-muted-foreground"
              onClick={() => {
                setShowUpload(false);
                setFile(null);
              }}
              disabled={isProcessing}
            >
              <X className="w-4 h-4" />
              Retour à la recherche
            </Button>

            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors",
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50",
                file && "border-primary bg-primary/5",
                isAnalyzing && "pointer-events-none opacity-70"
              )}
            >
              <input {...getInputProps()} />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium truncate max-w-[200px]">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} Ko
                    </p>
                  </div>
                  {!isAnalyzing && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {isDragActive
                      ? "Déposez le fichier ici..."
                      : "Glissez-déposez ou cliquez pour sélectionner"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF ou image, max 20 Mo
                  </p>
                </>
              )}
            </div>

            {/* AI Analysis Loading State */}
            {isAnalyzing && (
              <div className="flex items-center justify-center gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <div className="text-sm">
                  <span className="font-medium text-primary">Analyse IA en cours...</span>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Extraction du titre et classification
                  </p>
                </div>
              </div>
            )}

            {/* Analysis Complete - Show editable title */}
            {analysisComplete && file && (
              <div className="space-y-4">
                {analysisError ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="text-sm text-amber-700">
                      Analyse partielle - veuillez vérifier le titre
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <Sparkles className="w-4 h-4 text-green-600 shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-green-700">Document analysé</span>
                      {extractedData?.doc_type && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {DOC_TYPE_LABELS[extractedData.doc_type] || extractedData.doc_type}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="docTitle" className="flex items-center gap-2 text-sm font-medium">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    Titre du document
                  </Label>
                  <Input
                    id="docTitle"
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="Titre du document..."
                    className="font-medium"
                  />
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleUpload}
              disabled={!canUpload}
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Importer et lier
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Document Row Component
interface DocumentRowProps {
  document: Document;
  onSelect: () => void;
  onPreview: () => void;
  highlighted?: boolean;
}

function DocumentRow({ document, onSelect, onPreview, highlighted }: DocumentRowProps) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
        highlighted
          ? "bg-primary/5 hover:bg-primary/10 border border-primary/20"
          : "hover:bg-muted/50 border border-transparent"
      )}
    >
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <FileText className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{document.title}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {DOC_TYPE_LABELS[document.doc_type] || document.doc_type}
          </Badge>
          {document.issuer_name && (
            <span className="flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3" />
              {document.issuer_name}
            </span>
          )}
          {document.doc_date && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {format(new Date(document.doc_date), "MMM yyyy", { locale: fr })}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPreview();
        }}
        className="p-1.5 rounded-md hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground transition-colors"
        title="Prévisualiser"
      >
        <Eye className="w-4 h-4" />
      </button>
      {highlighted && (
        <Check className="w-4 h-4 text-primary shrink-0" />
      )}
    </button>
  );
}
